import { DialogUserLicenseUpgradeConfirm } from "@/components/team/dialog-user-license-upgrade-confirm";
import { DialogUserLicenseUpgradePreflight } from "@/components/team/dialog-user-license-upgrade-preflight";
import { useUserLicenseChange } from "@/components/team/use-user-license-change";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePrices } from "@/contexts/prices-provider";
import { useTeams } from "@/contexts/teams-provider";
import { TeamUserRolesEnum } from "@/enums/team-user-role";
import { useTranslation } from "@/hooks/useTranslation";
import { legacyBasicFrequency } from "@/lib/legacy-basic-price";
import { toHumanReadableAmount } from "@/lib/string";
import { cn } from "@/lib/utils";
import {
  TeamBillingPriceDTO,
  TeamLicenseInventoryDTO,
  TeamUserDTO,
} from "@shaarp/common";
import { useEffect } from "react";

/** Sentinel for the "no seat" option: Radix Select rejects an empty value. */
const NO_LICENCE_VALUE = "none";

type DataTableCellUserLicenceProps = {
  user: TeamUserDTO;
  /** Current license inventory for stock-aware display */
  inventory?: TeamLicenseInventoryDTO;
  /** Called after a change so the parent can refetch inventory */
  onInventoryRefetch?: () => Promise<void>;
};

/**
 * Cell component for the users data table displaying the license selector.
 * Stock-aware: shows all options but adds a localized "seat needed" badge when stock is 0.
 * Routes changes through useUserLicenseChange for simple-swap vs 2-step flow.
 */
export default function DataTableCellUserLicence({
  user,
  inventory,
  onInventoryRefetch,
}: DataTableCellUserLicenceProps) {
  const { t } = useTranslation();
  const { isAdmin, isManager } = useTeams();
  const { prices, apiFetchPrices } = usePrices();

  // Derived directly from props/context so an unassign (user.priceId → null)
  // immediately clears the displayed license instead of showing a stale value.
  const priceSelected = user.priceId
    ? prices.find((p) => p.id === user.priceId)
    : undefined;
  const pricesMonth = prices.filter((p) => p.interval === "month");
  const pricesYear = prices.filter((p) => p.interval === "year");

  // A seat held on a price the catalogue no longer lists — a Basic seat left
  // from before #744 — is shown, read-only, under its own name: an empty cell
  // reads as "no licence", and the first pick in it would buy a Pro seat.
  const heldUnlistedPriceLabel = (() => {
    if (!user.priceId || prices.length === 0 || priceSelected) return null;
    const frequency = legacyBasicFrequency(user.priceId);
    if (!frequency) return t("components.dataTableUsers.licence.unknownPlan");
    return `${t("components.dataTableUsers.licence.basicPlan")} (${t(
      `components.dataTableUsers.licence.${frequency === "annual" ? "annual" : "monthly"}`
    )})`;
  })();

  // Billing users have no licence to change
  // Admin can change any licence (including their own)
  // Manager can change licences for all users
  const hasPermission =
    user.role !== TeamUserRolesEnum.BILLING && (isAdmin || isManager);

  const licenseChange = useUserLicenseChange(onInventoryRefetch);

  useEffect(() => {
    apiFetchPrices();
  }, []);

  /**
   * Returns true if there is available stock for this price in the inventory.
   */
  const hasStock = (price: TeamBillingPriceDTO): boolean => {
    const available = getAvailable(price);
    return available === null || available > 0;
  };

  /**
   * Remaining licenses for a price, or null when availability is unknown
   * (no inventory provided or the price has no mapped licence type).
   */
  const getAvailable = (price: TeamBillingPriceDTO): number | null => {
    if (!inventory) return null;
    const freq =
      price.interval === "month" ? inventory.monthly : inventory.annual;
    if (price.licenceType === "professional") return freq.pro.available;
    return null;
  };

  /**
   * Badge shown next to an option: remaining-license count when stock is
   * available, or the "seat needed" hint when the pool is empty.
   */
  const renderStockBadge = (
    price: TeamBillingPriceDTO,
    inStock: boolean,
    disabled: boolean
  ) => {
    if (disabled) return null;
    const available = getAvailable(price);
    if (available !== null && available > 0) {
      return (
        <Badge
          variant="default"
          className="rounded-full text-[10px] px-2 py-0 ml-1"
        >
          {available}
        </Badge>
      );
    }
    if (!inStock) {
      return (
        <Badge
          variant="outline"
          className="text-orange-600 border-orange-300 text-[10px] py-0 ml-1"
        >
          {t("components.dataTableUsers.licence.seatNeeded", { count: 1 })}
        </Badge>
      );
    }
    return null;
  };

  const handleOnChange = async (newPriceId: string) => {
    if (!newPriceId || newPriceId === user.priceId) return;
    await licenseChange.initiateChange(user.id, newPriceId);
  };

  const handleFreePlanChange = async (value: string) => {
    if (value !== NO_LICENCE_VALUE) {
      await handleOnChange(value);
      return;
    }
    if (!user.priceId) return;
    await licenseChange.unassign(user.id);
  };

  // Resolve target price from the preview for modals
  const resolveTargetPrice = (): TeamBillingPriceDTO | undefined => {
    const preview = licenseChange.preview;
    if (!preview) return undefined;
    // Pro increased → target is pro at that frequency
    if (preview.poolAfter.pro > preview.poolBefore.pro) {
      return prices.find(
        (p) =>
          p.licenceType === "professional" &&
          p.interval === (preview.poolAfter.frequency === "monthly" ? "month" : "year")
      );
    }
    return undefined;
  };

  // For Billing role: show "Free" label with disabled selector
  if (user.role === TeamUserRolesEnum.BILLING) {
    return (
      <Select disabled value="free">
        <SelectTrigger>
          <SelectValue>
            {t("components.dataTableUsers.licence.free")}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="free">
            {t("components.dataTableUsers.licence.free")}
          </SelectItem>
        </SelectContent>
      </Select>
    );
  }

  // Shaarp staff always surface with the team's "admin" role and get full Pro
  // access without consuming a seat, so their licence is fixed and read-only.
  if (user.role === TeamUserRolesEnum.ADMIN) {
    const proLabel =
      prices.find((p) => p.licenceType === "professional")?.productName ??
      t("components.dataTableUsers.licence.proPlan");
    return (
      <Select disabled value="admin-pro">
        <SelectTrigger>
          <SelectValue>{proLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="admin-pro">{proLabel}</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  // Free (admin-managed) plans hold a fixed, admin-controlled stock of monthly
  // Pro seats: the manager takes seats from that stock and gives them back, but
  // cannot change a member's tier or frequency. So the only two states offered
  // are the monthly Pro seat and no seat at all.
  if (inventory?.freePlan) {
    const proPrice = pricesMonth.find(
      (price) => price.licenceType === "professional"
    );
    const isProAssigned = !!proPrice && priceSelected?.id === proPrice.id;
    const isProInStock = isProAssigned || inventory.monthly.pro.available > 0;
    // A seat held on any other price — listed (team converted from a paid
    // plan) or not (a Basic seat from before #744) — stays visible and
    // read-only: the cell must not claim the member has none, but only Pro and
    // no-seat are settable.
    const heldOtherPrice = isProAssigned ? undefined : priceSelected;

    return (
      <Select
        value={user.priceId ?? NO_LICENCE_VALUE}
        onValueChange={handleFreePlanChange}
        disabled={!hasPermission || !proPrice || licenseChange.isLoading}
      >
        <SelectTrigger>
          <SelectValue>
            {priceSelected?.productName ??
              heldUnlistedPriceLabel ??
              t("components.dataTableUsers.licence.noLicence")}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_LICENCE_VALUE}>
            {t("components.dataTableUsers.licence.noLicence")}
          </SelectItem>
          {heldOtherPrice && (
            <SelectItem value={heldOtherPrice.id} disabled>
              {heldOtherPrice.productName}
            </SelectItem>
          )}
          {user.priceId && heldUnlistedPriceLabel && (
            <SelectItem value={user.priceId} disabled>
              {heldUnlistedPriceLabel}
            </SelectItem>
          )}
          {proPrice && (
            <SelectItem value={proPrice.id} disabled={!isProInStock}>
              <span className="flex items-center gap-2">
                {proPrice.productName}
                {renderStockBadge(proPrice, isProInStock, false)}
              </span>
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    );
  }

  // Cross-frequency changes are supported only as a pure re-assignment against
  // existing stock (a seat purchase on another subscription still requires
  // cancel + re-assign), so an option in the other frequency stays selectable
  // only while that pool has an available license.
  const currentInterval = priceSelected?.interval;
  const isCrossFrequency = (price: TeamBillingPriceDTO): boolean =>
    !!currentInterval && price.interval !== currentInterval;

  const changeTargetPrice = resolveTargetPrice();

  return (
    <>
      <Select
        defaultValue=""
        value={priceSelected?.id || (heldUnlistedPriceLabel && user.priceId) || ""}
        onValueChange={handleOnChange}
        disabled={!hasPermission || licenseChange.isLoading}
      >
        <SelectTrigger>
          <SelectValue>
            {priceSelected
              ? `${priceSelected.productName} (${
                  priceSelected.interval === "year"
                    ? t("components.dataTableUsers.licence.annual")
                    : t("components.dataTableUsers.licence.monthly")
                })`
              : (heldUnlistedPriceLabel ?? "")}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {user.priceId && heldUnlistedPriceLabel && (
            <SelectItem value={user.priceId} disabled>
              {heldUnlistedPriceLabel}
            </SelectItem>
          )}
          {pricesMonth.length > 0 && (
            <SelectGroup>
              <SelectLabel>
                {t("components.dataTableUsers.licence.monthlyLabel")}
              </SelectLabel>
              {pricesMonth.map((price) => {
                const isCurrent = price.id === user.priceId;
                const inStock = isCurrent || hasStock(price);
                const disabled = isCrossFrequency(price) && !inStock;
                return (
                  <SelectItem
                    key={price.id}
                    value={price.id}
                    disabled={disabled}
                    className={cn(
                      price.licenceType === null && "bg-warning-foreground"
                    )}
                  >
                    <span className="flex items-center gap-2">
                      {price.productName} -{" "}
                      {toHumanReadableAmount(price.amount)}{" "}
                      {t("components.dataTableUsers.licence.perMonth")}
                      {renderStockBadge(price, inStock, disabled)}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectGroup>
          )}
          {pricesYear.length > 0 && (
            <SelectGroup>
              <SelectLabel>
                {t("components.dataTableUsers.licence.annualLabel")}
              </SelectLabel>
              {pricesYear.map((price) => {
                const isCurrent = price.id === user.priceId;
                const inStock = isCurrent || hasStock(price);
                const disabled = isCrossFrequency(price) && !inStock;
                return (
                  <SelectItem
                    key={price.id}
                    value={price.id}
                    disabled={disabled}
                    className={cn(
                      price.licenceType === null && "bg-warning-foreground"
                    )}
                  >
                    <span className="flex items-center gap-2">
                      {price.productName} -{" "}
                      {toHumanReadableAmount(price.amount)}{" "}
                      {t("components.dataTableUsers.licence.perYear")}
                      {renderStockBadge(price, inStock, disabled)}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>

      {/* Preflight modal — step 1 */}
      {licenseChange.step === "preflight" &&
        licenseChange.preview &&
        changeTargetPrice && (
          <DialogUserLicenseUpgradePreflight
            preview={licenseChange.preview}
            targetPrice={changeTargetPrice}
            onNext={licenseChange.advanceToConfirm}
            onClose={licenseChange.reset}
          />
        )}

      {/* Confirm modal — step 2 */}
      {licenseChange.step === "confirm" &&
        licenseChange.preview &&
        changeTargetPrice && (
          <DialogUserLicenseUpgradeConfirm
            preview={licenseChange.preview}
            targetPrice={changeTargetPrice}
            isLoading={licenseChange.isLoading}
            onConfirm={licenseChange.confirmChange}
            onBack={licenseChange.goBackToPreflight}
            onClose={licenseChange.reset}
          />
        )}
    </>
  );
}
